import { DateRange } from "react-day-picker";
import { z } from "zod";

import {
  DASHBOARD_SCHEDULE_DAYS,
  monthOptions,
  timezoneToIana,
} from "@/lib/constants";
import { DateFilterOption, Timezone } from "@/types";

/**
 * Ensures a timestamp string is interpreted as UTC.
 * Server timestamps from nova arrive without timezone info (e.g., "2026-04-10 01:26:22.631"),
 * which JavaScript parses as local time. This appends "Z" so they're treated as UTC.
 */
const ensureUtcTimestamp = (dateString: string): string => {
  if (/[Z]$/i.test(dateString) || /[+-]\d{2}:?\d{2}$/.test(dateString)) {
    return dateString;
  }
  return dateString.replace(" ", "T") + "Z";
};

const DAYS_PER_MONTH = 30;
const DAYS_IN_WEEK = 7;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MS_PER_DAY = MS_PER_HOUR * 24;

const calculateTimeDifferenceMs = ({
  date,
  shouldSetToStartOfDay = false,
}: {
  date: string;
  shouldSetToStartOfDay?: boolean;
}): number => {
  const now = new Date();
  const then = new Date(date);
  if (shouldSetToStartOfDay) {
    now.setHours(0, 0, 0, 0);
    then.setHours(0, 0, 0, 0);
  }
  return now.getTime() - then.getTime();
};

export const dates = {
  /**
   * Parses a server timestamp into a Date, treating timezone-naive strings
   * (nova's format) as UTC rather than letting JS assume local time.
   */
  parseUtcTimestamp: (dateString: string): Date =>
    new Date(ensureUtcTimestamp(dateString)),

  /**
   * Returns the ISO string of a date set to the start of day (00:00:00.000).
   */
  getStartOfDay: (date: Date): string => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  },

  /**
   * Returns the ISO string of the day after the given date, at start of day (00:00:00.000).
   */
  getStartOfNextDay: (date: Date): string => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  },

  /**
   * Formats a timestamp as a short month, day, and time (e.g. "May 27, 9:12 AM").
   */
  formatShortDateTime: (date: string): string =>
    new Date(date).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),

  /**
   * Formats a timestamp as a short month and day, including the year only when
   * it's not the current year (e.g. "May 27" this year, "May 27, 2025" earlier).
   */
  formatShortDate: (date: string): string => {
    const parsed = new Date(date);
    const isPastYear = parsed.getFullYear() !== new Date().getFullYear();
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(isPastYear && { year: "numeric" }),
    });
  },

  /**
   * Formats a timestamp as a numeric date (e.g. "05-27-2026").
   */
  formatNumericDate: (date: string): string =>
    new Date(date).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),

  /**
   * Formats a date as a full weekday name with month and day (e.g. "Monday, March 17").
   */
  formatDateHeader: (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  },

  /**
   * Formats a date as a full weekday, month, day, and year (e.g. "Sunday, May 3, 2026").
   */
  formatLongDate: (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  },

  /**
   * Formats a month + year pair as a long string (e.g. "May 2026"). 1-indexed
   * month matches how it's stored on the team_goal row.
   */
  formatMonthYear: (month: number, year: number): string =>
    new Date(year, month - 1, 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    }),

  /**
   * Calculates the number of hours ago from a given date.
   */
  calculateHoursAgo: (date: string): number => {
    const diff = calculateTimeDifferenceMs({ date });
    return Math.floor(diff / MS_PER_HOUR);
  },

  /**
   * Calculates fractional hours elapsed since a given date, rounded to 2 decimals.
   * Use when sub-hour precision matters (e.g. analytics) and whole-hour flooring would lose signal.
   */
  calculateHoursElapsed: (date: string): number => {
    const diff = calculateTimeDifferenceMs({ date });
    return Math.round((diff / MS_PER_HOUR) * 100) / 100;
  },

  /**
   * Calculates the number of minutes ago from a given date.
   */
  calculateMinutesAgo: (date: string): number => {
    const diff = calculateTimeDifferenceMs({ date });
    return Math.floor(diff / MS_PER_MINUTE);
  },

  /**
   * Calculates the number of days ago from a given date, setting the date and current date to the start of the day.
   */
  calculateDaysAgo: (date: string): number => {
    const diff = calculateTimeDifferenceMs({
      date,
      shouldSetToStartOfDay: true,
    });

    // Math.round can be used because shouldSetToStartOfDay is true. It is needed because of DST changes.
    // If DST is on 3/8/2026, then calculateTimeDifferenceMs(3/8, true) = 23/24 (or 0.95) which is floored to 0.
    // Something that happened 26 hours ago would be counted as 0 days ago, which is incorrect
    return Math.round(diff / MS_PER_DAY);
  },

  /**
   * Checks if two dates are equal, ignoring the time component.
   */
  checkIfEqualDates: (date1: string | null, date2: string | null): boolean => {
    if (!date1 || !date2) return false;
    return new Date(date1).toDateString() === new Date(date2).toDateString();
  },

  /**
   * Converts a human-readable date string to an ISO date string.
   */
  convertStringDateToIso: (dateString: string | null): string | null => {
    if (!dateString) return null;
    return new Date(dateString).toISOString();
  },

  /**
   * Converts a datetime-local string to an ISO string for sending to the BE.
   * Returns null if the input is empty, null, or undefined.
   */
  datetimeLocalToIso: (
    datetimeLocalString: string | null | undefined,
  ): string | null => {
    if (!datetimeLocalString) return null;

    try {
      // Create a Date object from the datetime-local string.
      // This will interpret the string in the user's local timezone
      const date = new Date(datetimeLocalString);
      if (isNaN(date.getTime())) return null;

      // Convert to ISO string
      return date.toISOString();
    } catch {
      return null;
    }
  },

  /**
   * Converts a datetime-local string (e.g. "2026-03-31T10:00") interpreted in
   * the given IANA timezone to a UTC ISO string.
   *
   * Unlike `datetimeLocalToIso` which uses the browser's local timezone,
   * this interprets the naive datetime in the specified timezone.
   */
  datetimeLocalToIsoInTimezone: (
    datetimeLocalString: string,
    timezone: Timezone,
  ): string | null => {
    try {
      const iana = timezoneToIana[timezone];
      const [datePart, timePart] = datetimeLocalString.split("T");
      if (!datePart || !timePart) return null;

      const [year, month, day] = datePart.split("-").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);

      // Treat the input as if it were UTC.
      const asUtc = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      if (isNaN(asUtc.getTime())) return null;

      // Find the wall-clock time in the target timezone for that UTC instant.
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: iana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(asUtc);

      const get = (type: string) =>
        parts.find((p) => p.type === type)?.value ?? "0";

      const tzWallAsUtc = Date.UTC(
        Number(get("year")),
        Number(get("month")) - 1,
        Number(get("day")),
        Number(get("hour")),
        Number(get("minute")),
        Number(get("second")),
      );

      // Offset = Wall clock time - UTC time. Subtract offset to go from wall clock time to UTC time.
      const offsetMs = tzWallAsUtc - asUtc.getTime();
      return new Date(asUtc.getTime() - offsetMs).toISOString();
    } catch {
      return null;
    }
  },

  /**
   * Formats a date as a short day name with month, day, and ordinal suffix.
   * Returns a string like "Mon, Dec 29th".
   */
  formatDayWithOrdinal: (date: Date): string => {
    const dayName = date.toLocaleDateString("en-US", { weekday: "short" });
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    return `${dayName}, ${month} ${String(day)}${dates.getDaySuffix(day)}`;
  },

  /**
   * Formats an ISO date string as a relative date label ("Today", "Yesterday") or a short date (e.g. "Mar 10, 2026").
   */
  formatDateDivider: (isoString: string): string => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  },

  /**
   * Formats a date-only string (e.g. "2024-01-15") stored in a JSON column.
   * Uses UTC timezone to prevent local timezone from shifting the day.
   */
  formatUtcDateForUser: (
    dateString: string | null | undefined,
  ): string | null => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  },

  /**
   * Converts an ISO date string to a human-readable date string for user activity.
   */
  formatIsoDateForUser: (
    dateString: string | null | undefined,
  ): string | null => {
    if (!dateString) return null;
    return new Date(ensureUtcTimestamp(dateString)).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      },
    );
  },

  /**
   * Converts an ISO datetime string to a human-readable date and time string.
   */
  formatIsoDateTimeForUser: (
    dateString: string | null | undefined,
  ): string | null => {
    if (!dateString) return null;
    return new Date(ensureUtcTimestamp(dateString)).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  },

  /**
   * Formats a date as a short time string (e.g. "2:30 PM"). Accepts an ISO
   * string or Date; pass an IANA `timeZone` to render in that zone instead of
   * the viewer's local zone.
   */
  formatTime: (input: string | Date, timeZone?: string): string => {
    const d = typeof input === "string" ? new Date(input) : input;
    return d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...(timeZone ? { timeZone } : {}),
    });
  },

  calculateEndTime: (start: Date, durationMinutes: number) => {
    return new Date(start.getTime() + durationMinutes * 60 * 1000);
  },

  /**
   * Formats a start/end time range (e.g. "2:00 PM – 2:30 PM"). Falls back to
   * just the start when no duration is provided.
   */
  formatTimeRange: (
    start: Date,
    durationMinutes: number | null,
    timeZone?: string,
  ): string => {
    const startLabel = dates.formatTime(start, timeZone);
    if (durationMinutes === null || durationMinutes <= 0) return startLabel;
    const end = dates.calculateEndTime(start, durationMinutes);
    return `${startLabel} – ${dates.formatTime(end, timeZone)}`;
  },

  /**
   * Compact single-time label used in dense surfaces (schedule cards):
   *   10:00 AM → "10am"
   *   9:30 AM  → "9:30am"
   * Drops ":00" minutes; lowercases the am/pm suffix and drops the space.
   */
  formatTimeShort: (date: Date, timeZone?: string): string => {
    const parts = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...(timeZone ? { timeZone } : {}),
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")?.value ?? "";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "";
    const period = (
      parts.find((p) => p.type === "dayPeriod")?.value ?? ""
    ).toLowerCase();
    return minute === "00" ? `${hour}${period}` : `${hour}:${minute}${period}`;
  },

  /**
   * Compact start/end time range used in dense surfaces (schedule cards):
   *   10:00 + 30m  → "10-10:30am"
   *   11:30 + 90m  → "11:30am-1pm"
   *   9:30  + 30m  → "9:30-10am"
   * Drops ":00" minutes and collapses the am/pm suffix when both halves
   * share it. Falls back to `formatTimeShort` when no duration is provided.
   */
  formatTimeRangeShort: (
    start: Date,
    durationMinutes: number | null,
    timeZone?: string,
  ): string => {
    if (durationMinutes === null || durationMinutes <= 0) {
      return dates.formatTimeShort(start, timeZone);
    }
    const end = dates.calculateEndTime(start, durationMinutes);

    const parse = (
      d: Date,
    ): { hour: string; minute: string; period: string } => {
      const parts = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        ...(timeZone ? { timeZone } : {}),
      }).formatToParts(d);
      const hour = parts.find((p) => p.type === "hour")?.value ?? "";
      const minute = parts.find((p) => p.type === "minute")?.value ?? "";
      const period = (
        parts.find((p) => p.type === "dayPeriod")?.value ?? ""
      ).toLowerCase();
      return { hour, minute, period };
    };

    const partLabel = ({ hour, minute }: { hour: string; minute: string }) =>
      minute === "00" ? hour : `${hour}:${minute}`;

    const s = parse(start);
    const e = parse(end);
    if (s.period === e.period) {
      return `${partLabel(s)}-${partLabel(e)}${e.period}`;
    }
    return `${partLabel(s)}${s.period}-${partLabel(e)}${e.period}`;
  },

  /**
   * The viewer's resolved IANA timezone (e.g. "America/Los_Angeles").
   */
  viewerTimezoneIana: (): string =>
    Intl.DateTimeFormat().resolvedOptions().timeZone,

  /**
   * Midnight of the given date in the viewer's local timezone.
   */
  getStartOfLocalDay: (d: Date): Date => {
    const c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
  },

  /**
   * Resolve dates correctly for those not timestamped
   */
  taskDueDate: (dueDate: string): Date => {
    const parsed = new Date(dueDate);
    const isDateOnlyUtc =
      parsed.getUTCHours() === 0 &&
      parsed.getUTCMinutes() === 0 &&
      parsed.getUTCSeconds() === 0 &&
      parsed.getUTCMilliseconds() === 0;
    if (!isDateOnlyUtc) return parsed;
    return new Date(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
    );
  },

  /**
   * The shared 7-day schedule window: viewer-local start-of-today (inclusive)
   * → +DASHBOARD_SCHEDULE_DAYS (exclusive), as ISO strings. One source for the
   * dashboard + job-pipeline calendar queries so their windows can't drift.
   */
  scheduleWindow: (): { startDate: string; endDate: string } => {
    const start = dates.getStartOfLocalDay(new Date());
    const end = new Date(start);
    end.setDate(start.getDate() + DASHBOARD_SCHEDULE_DAYS);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  },

  /**
   * Viewer-local midnight of the Sunday starting the week that contains `d`.
   */
  getSundayWeekStart: (d: Date): Date => {
    const start = dates.getStartOfLocalDay(d);
    start.setDate(start.getDate() - start.getDay());
    return start;
  },

  /**
   * A single Sun–Sat fetch window: `weekStart` (inclusive) → +7 days
   * (exclusive), as ISO strings.
   */
  weekWindow: (weekStart: Date): { startDate: string; endDate: string } => {
    const end = new Date(weekStart);
    end.setDate(weekStart.getDate() + DAYS_IN_WEEK);
    return { startDate: weekStart.toISOString(), endDate: end.toISOString() };
  },

  /**
   * `days` calendar days from `d` in the viewer's local timezone, anchored to
   * midnight so DST shifts can't drift the result.
   */
  addLocalDays: (d: Date, days: number): Date => {
    const c = dates.getStartOfLocalDay(d);
    c.setDate(c.getDate() + days);
    return c;
  },

  /**
   * Stable YYYY-MM-DD key in the viewer's local timezone for grouping by day
   * without DST drift.
   */
  localDayKey: (d: Date): string => {
    const c = dates.getStartOfLocalDay(d);
    const y = c.getFullYear().toString();
    const m = (c.getMonth() + 1).toString().padStart(2, "0");
    const day = c.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${day}`;
  },

  /**
   * Relative day label in the viewer's local timezone: "Yesterday" / "Today" /
   * "Tomorrow", otherwise "Wed, Jul 15".
   */
  relativeDayLabel: (d: Date, now: Date = new Date()): string => {
    const key = dates.localDayKey(d);
    if (key === dates.localDayKey(dates.addLocalDays(now, -1)))
      return "Yesterday";
    if (key === dates.localDayKey(now)) return "Today";
    if (key === dates.localDayKey(dates.addLocalDays(now, 1)))
      return "Tomorrow";
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  },

  /**
   * Formats a positive millisecond diff as a short countdown (e.g. "3h 24m",
   * "24m", "<1m"). Values <= 0 are treated as "<1m".
   */
  formatCountdown: (diffMs: number): string => {
    const totalMinutes = Math.floor(diffMs / (60 * 1000));
    if (totalMinutes < 1) return "<1m";
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${String(minutes)}m`;
    return `${String(hours)}h ${String(minutes)}m`;
  },

  /**
   * Formats a positive millisecond diff as a compact two-unit label that
   * keeps a non-zero second unit when present:
   *   ≥ 1 day  → "Xd Yh" (or "Xd" when hours == 0)
   *   ≥ 1 hour → "Xh Ym" (or "Xh" when minutes == 0)
   *   < 1 hour → "Xm"   (or "<1m" when minutes < 1)
   *
   * Use for countdowns + delta displays where rounding away the smaller
   * unit would misrepresent the gap (e.g. 1d 23h → "1d" is misleading).
   */
  formatDuration: (diffMs: number): string => {
    const totalMinutes = Math.floor(diffMs / MS_PER_MINUTE);
    if (totalMinutes < 1) return "<1m";
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) {
      return hours === 0
        ? `${String(days)}d`
        : `${String(days)}d ${String(hours)}h`;
    }
    if (hours > 0) {
      return minutes === 0
        ? `${String(hours)}h`
        : `${String(hours)}h ${String(minutes)}m`;
    }
    return `${String(minutes)}m`;
  },

  /**
   * Formats an ISO datetime for a near-term deadline display in the user's
   * local timezone (e.g. "Tue, Mar 5, 3:45 PM"). Year is omitted.
   */
  formatDeadlineForUser: (iso: string): string =>
    new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),

  /**
   * Formats a week start date (Sunday) into a human-readable week range string.
   * Returns a string like "Jan 1, 2024 - Jan 7, 2024" or "Dec 29, 2023 - Jan 4, 2024".
   */
  formatWeekRange: (weekStart: Date): string => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const startMonth = weekStart.toLocaleDateString("en-US", {
      month: "short",
    });
    const startDay = weekStart.getDate();
    const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
    const endDay = weekEnd.getDate();
    const startYear = weekStart.getFullYear();
    const endYear = weekEnd.getFullYear();

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${startMonth} ${startDay}${startYear !== endYear ? `, ${startYear}` : ""} - ${startMonth !== endMonth ? `${endMonth} ` : ""}${endDay}, ${endYear}`;
  },

  /**
   * Gets the dates for each day of the week starting from the given week start date.
   */
  getWeekDates: (weekStart: string): Date[] => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      return date;
    });
  },

  /**
   * Formats a date as "Mon Dth, YYYY - h:mm AM/PM" — used in ping tooltips.
   */
  formatExactDate: (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = date.getDate();
    const suffix = dates.getDaySuffix(day);
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${month} ${String(day)}${suffix}, ${String(year)} - ${time}`;
  },

  /**
   * Gets the ordinal suffix for a day (st, nd, rd, th).
   */
  getDaySuffix: (day: number): string => {
    if (day >= 11 && day <= 13) {
      return "th";
    }
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  },

  /**
   * Gets the start date of the next week for a given date (aka the following Sunday)
   */
  getWeekEndDate: (date: Date): Date => {
    const mostRecentSunday = dates.getWeekStartDate(date);
    return new Date(mostRecentSunday.setDate(mostRecentSunday.getDate() + 7));
  },

  /**
   * Gets the most recent Sunday (start of the week) for a given date.
   * Sets hours to 0:00:00:000 and returns the Sunday of that week.
   */
  getWeekStartDate: (date: Date): Date => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return new Date(newDate.setDate(newDate.getDate() - newDate.getDay()));
  },

  /**
   * Handles the selection of a predefined date range.
   * Determines startDate and endDate based on current date and selected range.
   * Both date selections in this function are set to represent being inclusive of the entire day.
   * End dates are increased by 1 day in misc.prepareQueryParams() before being sent to the BE to act inclusive.
   * Adding +1 to the endDate here would cause the custom date range picker to display incorrectly.
   */
  handleDateRangeFilter: (dateAcceptedFilter: DateFilterOption): DateRange => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    let startDate: Date | undefined = new Date(date);
    let endDate: Date | undefined = new Date(date);
    if (dateAcceptedFilter === DateFilterOption.Today) {
      endDate.setDate(endDate.getDate());
    } else if (dateAcceptedFilter === DateFilterOption.Yesterday) {
      startDate.setDate(startDate.getDate() - 1);
      endDate.setDate(endDate.getDate() - 1);
    } else if (dateAcceptedFilter === DateFilterOption.LastWeek) {
      // Last week's Saturday is 1-7 days ago (.getDay(): 0=Sun); back up 6 more
      // days from a clone for the Sunday, so the offset crosses months cleanly.
      endDate.setDate(endDate.getDate() - endDate.getDay() - 1);
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 6);
    } else if (dateAcceptedFilter === DateFilterOption.ThisWeek) {
      // This Sunday is 0-6 days ago (.getDay(): 0=Sun); the Saturday is 6 days
      // forward from a clone, so the offset crosses months cleanly.
      startDate.setDate(startDate.getDate() - startDate.getDay());
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
    } else if (dateAcceptedFilter === DateFilterOption.LastMonth) {
      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
    } else if (dateAcceptedFilter === DateFilterOption.ThisMonth) {
      startDate = new Date(date.getFullYear(), date.getMonth(), 1);
      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    } else if (dateAcceptedFilter === DateFilterOption.LastQuarter) {
      // Quarter index 0-3 (Jan-Mar=0, ...); back up one quarter. A negative
      // month rolls the year so Q1 maps to last year's Q4 cleanly.
      const quarter = Math.floor(date.getMonth() / 3);
      startDate = new Date(date.getFullYear(), (quarter - 1) * 3, 1);
      endDate = new Date(date.getFullYear(), quarter * 3, 0);
    } else if (dateAcceptedFilter === DateFilterOption.ThisQuarter) {
      const quarter = Math.floor(date.getMonth() / 3);
      startDate = new Date(date.getFullYear(), quarter * 3, 1);
      endDate = new Date(date.getFullYear(), quarter * 3 + 3, 0);
    } else if (dateAcceptedFilter === DateFilterOption.ThisYear) {
      startDate = new Date(date.getFullYear(), 0, 1);
      endDate = new Date(date.getFullYear() + 1, 0, 0);
    } else {
      startDate = undefined;
      endDate = undefined;
    }

    // Ensure the start and end dates are set to the start of the day
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(0, 0, 0, 0);

    return {
      from: startDate,
      to: endDate,
    };
  },

  /**
   * Converts an ISO date string to the format expected by datetime-local inputs.
   * Returns empty string if the input is null, undefined, or empty.
   */
  isoToDatetimeLocal: (isoString: string | null | undefined): string => {
    if (!isoString) return "";

    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return "";

      // Format as YYYY-MM-DDTHH:mm (datetime-local format).
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${String(year)}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  },

  /**
   * Converts a UTC ISO string to a datetime-local string formatted in
   * the specified IANA timezone.
   *
   * Unlike `isoToDatetimeLocal` which uses the browser's local timezone,
   * this formats the datetime as it would appear in the given timezone.
   */
  isoToDatetimeLocalInTimezone: (
    isoString: string,
    timezone: Timezone,
  ): string => {
    if (!isoString) return "";

    try {
      const iana = timezoneToIana[timezone];
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return "";

      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: iana,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(date);

      const get = (type: string) =>
        parts.find((p) => p.type === type)?.value ?? "00";

      return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
    } catch {
      return "";
    }
  },

  /**
   * Returns a datetime-local string in the given timezone for the current time
   * minus `minutesAgo`, rounded to the nearest 5-minute mark.
   */
  roundedMinutesAgoDatetimeLocal: (
    minutesAgo: number,
    timezone: Timezone,
  ): string => {
    const MINUTE_ROUNDING_MS = 5 * MS_PER_MINUTE;
    const target =
      Math.round(
        (Date.now() - minutesAgo * MS_PER_MINUTE) / MINUTE_ROUNDING_MS,
      ) * MINUTE_ROUNDING_MS;
    return dates.isoToDatetimeLocalInTimezone(
      new Date(target).toISOString(),
      timezone,
    );
  },

  /**
   * Calculates tenure and returns it as string for display on profile
   */
  calculateTenureForCertificationOrEducationOrWorkExperience: ({
    startMonth,
    startYear,
    endMonth,
    endYear,
    current,
  }: {
    startMonth: string | null | undefined;
    startYear: string | null | undefined;
    endMonth: string | null | undefined;
    endYear: string | null | undefined;
    current: boolean | null | undefined;
  }): string => {
    const UNSPECIFIED = "Unspecified";

    // Resolve a month label to its 0-based index; null when missing or unknown.
    const toMonthIndex = (month: string | null | undefined): number | null => {
      if (!month || month === UNSPECIFIED) return null;
      const index = monthOptions.findIndex((m) => m.label === month);
      return index < 0 ? null : index;
    };

    // Parse a year string to a number; null when missing or unspecified.
    const toYear = (year: string | null | undefined): number | null => {
      if (!year || year === UNSPECIFIED) return null;
      const parsed = Number(year);
      return isNaN(parsed) ? null : parsed;
    };

    // Start year is mandatory; without it there's nothing to measure from.
    const startYearNum = toYear(startYear);
    if (startYearNum === null) return "";
    const startMonthIndex = toMonthIndex(startMonth);

    // A supplied end date wins over the current flag so the tenure agrees with
    // the date range shown beside it; today is only the fallback for a current
    // role with no end date.
    const now = new Date();
    let endYearNum = toYear(endYear);
    let endMonthIndex = endYearNum === null ? null : toMonthIndex(endMonth);
    if (endYearNum === null && current) {
      endYearNum = now.getFullYear();
      endMonthIndex = now.getMonth();
    }
    if (endYearNum === null) return "";

    // Both months known: give an exact span.
    if (startMonthIndex !== null && endMonthIndex !== null) {
      const totalMonths =
        (endYearNum - startYearNum) * 12 +
        (endMonthIndex - startMonthIndex) +
        1;
      if (totalMonths <= 0) return "";

      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;
      if (years > 0 && months > 0)
        return `${String(years)} yr ${String(months)} mo`;
      if (years > 0) return `${String(years)} yr`;
      return `${String(months)} mo`;
    }

    // A month is missing: estimate from the year gap alone.
    const yearGap = endYearNum - startYearNum;
    if (yearGap < 0) return "";
    if (yearGap === 0) return "< 1 yr";
    return `~${String(yearGap)}–${String(yearGap + 1)} yr`;
  },

  prepareDateDisplayForCertificationOrEducationOrWorkExperience: ({
    startMonth,
    startYear,
    endMonth,
    endYear,
    current,
  }: {
    startMonth: string | null | undefined;
    startYear: string | null | undefined;
    endMonth: string | null | undefined;
    endYear: string | null | undefined;
    current: boolean | null | undefined;
  }): string => {
    let start = "";
    if (startMonth === "Unspecified" && startYear === "Unspecified") {
      start = "Unspecified";
    } else if (startMonth == null && startYear != null) {
      start = startYear;
    } else if (startMonth != null && startYear != null) {
      start = `${startMonth} ${startYear}`;
    }

    let end = "";
    if (endMonth === "Unspecified" && endYear === "Unspecified") {
      end = "Unspecified";
    } else if (endMonth == null && endYear != null) {
      end = endYear;
    } else if (endMonth != null && endYear != null) {
      end = `${endMonth} ${endYear}`;
    } else if (current) {
      end = "Present";
    }

    if (start === "Unspecified" && end === "Unspecified") {
      return "Unspecified";
    } else if (!start && end) {
      return end;
    } else if (start && !end) {
      return start;
    } else if (start && end) {
      return `${start} - ${end}`;
    }
    return "";
  },

  /**
   * Precise short elapsed time — "12m ago", "1hr 30m ago", "3 days ago".
   */
  shortTimeAgo: (date: string): string => {
    const minutes = dates.calculateMinutesAgo(date);
    if (minutes < 1) return "just now";
    if (minutes < MINUTES_PER_HOUR) return `${minutes.toString()}m ago`;

    const hours = Math.floor(minutes / MINUTES_PER_HOUR);
    if (hours < HOURS_PER_DAY) {
      const trailingMinutes = minutes % MINUTES_PER_HOUR;
      if (trailingMinutes === 0) return `${hours.toString()}hr ago`;
      return `${hours.toString()}hr ${trailingMinutes.toString()}m ago`;
    }

    const days = Math.floor(hours / HOURS_PER_DAY);
    return `${days.toString()} ${days === 1 ? "day" : "days"} ago`;
  },

  /**
   * Converts a date into a human-readable relative time string for jobs.
   */
  timeAgoForJob: (date: string): string => {
    const daysAgo = dates.calculateDaysAgo(date);

    if (daysAgo === 0) {
      return "today";
    }
    if (daysAgo === 1) {
      return "yesterday";
    }
    if (daysAgo < DAYS_PER_MONTH * 3) {
      return `${daysAgo.toString()} days ago`;
    }

    const monthsAgo = Math.floor(daysAgo / DAYS_PER_MONTH);
    if (monthsAgo < 12) {
      return `${monthsAgo.toString()} months ago`;
    }
    return "> 1 year ago";
  },

  /**
   * Converts a date into a human-readable relative time string for pipeline activity.
   */
  timeAgoForPipeline: (date: string): string => {
    const minutesAgo = dates.calculateMinutesAgo(date);
    const hoursAgo = dates.calculateHoursAgo(date);
    const daysAgo = dates.calculateDaysAgo(date);

    if (minutesAgo < 1) {
      return "just now";
    }
    if (minutesAgo === 1) {
      return "1 minute ago";
    }
    if (minutesAgo < 60) {
      return `${minutesAgo.toString()} minutes ago`;
    }
    if (hoursAgo === 1) {
      return "1 hour ago";
    }
    if (hoursAgo < 24) {
      return `${hoursAgo.toString()} hours ago`;
    }
    if (daysAgo === 1) {
      return "1 day ago";
    }
    if (daysAgo < DAYS_PER_MONTH * 3) {
      return `${daysAgo.toString()} days ago`;
    }
    const monthsAgo = Math.floor(daysAgo / DAYS_PER_MONTH);
    if (monthsAgo < 12) {
      return `${monthsAgo.toString()} months ago`;
    }
    return "> 1 year ago";
  },

  /**
   * Same as above but without the " ago" suffix for compact display.
   */
  timeAgoForPipelineCompact: (date: string): string =>
    dates.timeAgoForPipeline(date).replace(/ ago$/, ""),

  /**
   * Same as timeAgoForPipeline but with abbreviated minutes (e.g. "30 min ago").
   */
  timeAgoForComment: (date: string): string =>
    dates.timeAgoForPipeline(date).replace(/minutes?/, "min"),

  /**
   * Validates that a starting date is equal to or before an ending date.
   *
   * If the starting date is after the ending date, an error is added to the context.
   */
  validateDatesForForm: (
    data: Record<string, unknown>,
    ctx: z.RefinementCtx,
    isCertificate: boolean,
  ): void => {
    const startYear = Number(data.startYear);
    const endYear = Number(data.endYear);

    if (!startYear || !endYear) {
      return;
    }

    // Ensure that end date is >= start date.
    if (endYear < startYear) {
      ctx.addIssue({
        code: "custom",
        message: `${isCertificate ? "Expiration" : "End"} Year must be greater than or equal to ${isCertificate ? "Issue" : "Start"} Year`,
        path: ["endYear"],
      });
    } else if (endYear === startYear) {
      if (data.endMonth && data.startMonth) {
        const startIndex = monthOptions.findIndex(
          (month) => month.label === data.startMonth,
        );
        const endIndex = monthOptions.findIndex(
          (month) => month.label === data.endMonth,
        );
        if (endIndex < startIndex) {
          ctx.addIssue({
            code: "custom",
            message: `${isCertificate ? "Expiration" : "End"} Date must be greater than or equal to ${isCertificate ? "Issue" : "Start"} Date`,
            path: ["endYear"],
          });
        }
      }
    }
  },
};
